<template>
  <div class="min-w-0 space-y-5">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex flex-wrap items-center gap-2">
        <span class="panel-glass-chip inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold">
          <span class="relative flex size-2 shrink-0 overflow-hidden rounded-full">
            <span
              class="absolute inline-flex size-full animate-ping rounded-full opacity-60"
              :class="overallOk ? 'bg-emerald-400' : 'bg-rose-400'"
            />
            <span
              class="relative inline-flex size-2 rounded-full"
              :class="overallOk ? 'bg-emerald-500' : 'bg-rose-500'"
            />
          </span>
          {{ __('System Monitor') }}
        </span>
        <span v-if="data?.host" class="text-xs text-zinc-500 dark:text-zinc-400">
          {{ data.host }}
        </span>
      </div>

      <div class="flex flex-wrap items-center gap-3 text-sm text-zinc-500 dark:text-zinc-400">
        <button
          type="button"
          class="panel-glass-btn rounded-full px-3 py-1.5 text-xs font-semibold"
          :disabled="loading || restartingKey"
          @click="fetchSnapshot"
        >
          {{ loading ? __('Refreshing…') : __('Refresh') }}
        </button>
        <button
          type="button"
          class="rounded-full bg-rose-500/15 px-3 py-1.5 text-xs font-semibold text-rose-600 dark:text-rose-400"
          :disabled="!!restartingKey"
          @click="restart('all')"
        >
          {{ restartingKey === 'all' ? __('Restarting…') : __('Restart all services') }}
        </button>
        <span v-if="updatedLabel">{{ updatedLabel }}</span>
      </div>
    </div>

    <div v-if="error" class="panel-glass rounded-[2rem] p-4 text-sm text-rose-500">
      <div class="panel-glass-content">{{ error }}</div>
    </div>

    <div v-if="actionMessage" class="panel-glass rounded-[2rem] p-4 text-sm text-emerald-600 dark:text-emerald-400">
      <div class="panel-glass-content">{{ actionMessage }}</div>
    </div>

    <div class="grid min-w-0 gap-4 md:grid-cols-3">
      <article class="panel-glass rounded-[2rem] p-4 md:p-5">
        <div class="panel-glass-content space-y-3">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold text-zinc-900 dark:text-white">{{ __('CPU') }}</h3>
            <span class="text-xs font-bold" :class="toneClass(cpuPercent)">
              {{ cpuPercent != null ? cpuPercent + '%' : '—' }}
            </span>
          </div>
          <div class="h-2.5 overflow-hidden rounded-full bg-zinc-200/70 dark:bg-zinc-800">
            <div
              class="h-full rounded-full transition-all duration-500"
              :class="barClass(cpuPercent)"
              :style="{ width: meterWidth(cpuPercent) }"
            />
          </div>
          <p class="text-[11px] text-zinc-500 dark:text-zinc-400">
            {{ __('Load') }}:
            {{ data?.cpu?.load_1 ?? '—' }} /
            {{ data?.cpu?.load_5 ?? '—' }} /
            {{ data?.cpu?.load_15 ?? '—' }}
            · {{ data?.cpu?.cores ?? '—' }} {{ __('cores') }}
          </p>
        </div>
      </article>

      <article class="panel-glass rounded-[2rem] p-4 md:p-5">
        <div class="panel-glass-content space-y-3">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold text-zinc-900 dark:text-white">{{ __('RAM') }}</h3>
            <span class="text-xs font-bold" :class="toneClass(memPercent)">
              {{ memPercent != null ? memPercent + '%' : '—' }}
            </span>
          </div>
          <div class="h-2.5 overflow-hidden rounded-full bg-zinc-200/70 dark:bg-zinc-800">
            <div
              class="h-full rounded-full transition-all duration-500"
              :class="barClass(memPercent)"
              :style="{ width: meterWidth(memPercent) }"
            />
          </div>
          <p class="text-[11px] text-zinc-500 dark:text-zinc-400">
            {{ formatMb(data?.memory?.used_mb) }} / {{ formatMb(data?.memory?.total_mb) }}
            · {{ __('Free') }}: {{ formatMb(data?.memory?.free_mb) }}
          </p>
        </div>
      </article>

      <article class="panel-glass rounded-[2rem] p-4 md:p-5">
        <div class="panel-glass-content space-y-3">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold text-zinc-900 dark:text-white">{{ __('Disk') }}</h3>
            <span class="text-xs font-bold" :class="toneClass(diskPercent)">
              {{ diskPercent != null ? diskPercent + '%' : '—' }}
            </span>
          </div>
          <div class="h-2.5 overflow-hidden rounded-full bg-zinc-200/70 dark:bg-zinc-800">
            <div
              class="h-full rounded-full transition-all duration-500"
              :class="barClass(diskPercent)"
              :style="{ width: meterWidth(diskPercent) }"
            />
          </div>
          <p class="text-[11px] text-zinc-500 dark:text-zinc-400">
            {{ data?.disk?.used_gb ?? '—' }} / {{ data?.disk?.total_gb ?? '—' }} GB
            · {{ __('Free') }}: {{ data?.disk?.free_gb ?? '—' }} GB
          </p>
        </div>
      </article>
    </div>

    <article class="panel-glass rounded-[2rem] p-4 md:p-5">
      <div class="panel-glass-content space-y-4">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h3 class="text-sm font-semibold text-zinc-900 dark:text-white">{{ __('Workers') }}</h3>
          <span class="text-xs text-zinc-500 dark:text-zinc-400">
            {{ data?.workers?.summary?.active ?? 0 }} / {{ data?.workers?.summary?.total ?? 0 }}
            {{ __('active') }}
            · {{ data?.workers?.summary?.process_count ?? 0 }} {{ __('processes') }}
          </span>
        </div>

        <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div
            v-for="worker in data?.workers?.items || []"
            :key="worker.key"
            class="panel-glass-card rounded-3xl p-4"
          >
            <div class="panel-glass-content space-y-2">
              <div class="flex items-start justify-between gap-2">
                <p class="text-xs font-semibold text-zinc-900 dark:text-white">{{ worker.label }}</p>
                <span
                  class="shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold tracking-wide"
                  :class="worker.active
                    ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                    : 'bg-rose-500/15 text-rose-600 dark:text-rose-400'"
                >
                  {{ worker.active ? __('Active') : __('Down') }}
                </span>
              </div>
              <p class="text-[11px] text-zinc-500 dark:text-zinc-400">
                {{ __('Count') }}: <b class="text-zinc-800 dark:text-zinc-200">{{ worker.count }}</b>
                <span v-if="worker.systemd?.available">
                  · systemd: {{ worker.systemd.state }}
                </span>
                <span v-else>· systemd: —</span>
              </p>
              <p
                v-if="worker.hint"
                class="text-[11px] text-amber-600 dark:text-amber-400"
              >
                {{ worker.hint }}
              </p>
              <p
                v-if="worker.processes?.length"
                class="truncate font-mono text-[10px] text-zinc-400"
                :title="worker.processes[0].cmd"
              >
                pid {{ worker.processes[0].pid }}
              </p>
              <button
                v-if="worker.restartable !== false"
                type="button"
                class="panel-glass-btn mt-1 rounded-full px-3 py-1.5 text-[11px] font-semibold"
                :disabled="!!restartingKey"
                @click="restart(worker.key)"
              >
                {{ restartingKey === worker.key
                  ? __('Restarting…')
                  : (worker.key === 'scheduler'
                    ? (worker.systemd?.state === 'missing' ? __('Enable scheduler') : __('Clear lock'))
                    : __('Restart')) }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </article>

    <article class="panel-glass rounded-[2rem] p-4 md:p-5">
      <div class="panel-glass-content space-y-4">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h3 class="text-sm font-semibold text-zinc-900 dark:text-white">{{ __('Global logs') }}</h3>
          <div class="flex flex-wrap items-center gap-3">
            <button
              type="button"
              class="rounded-full bg-rose-500/15 px-3 py-1.5 text-xs font-semibold text-rose-600 dark:text-rose-400"
              :disabled="clearingLog"
              @click="clearLaravelLog"
            >
              {{ clearingLog ? __('Clearing…') : __('Clear Laravel log') }}
            </button>
            <label class="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
              <input v-model="autoScroll" type="checkbox" class="rounded border-zinc-400">
              {{ __('Auto-scroll') }}
            </label>
          </div>
        </div>

        <div v-if="!(data?.logs?.length)" class="text-sm text-zinc-500">
          {{ __('No readable log files found.') }}
        </div>

        <div v-else class="flex flex-wrap gap-2">
          <button
            v-for="log in data.logs"
            :key="log.key"
            type="button"
            class="panel-glass-btn rounded-full px-3 py-1.5 text-xs font-semibold"
            :class="{ 'panel-glass-btn--active': selectedLog === log.key }"
            @click="selectedLog = log.key"
          >
            {{ log.label }}
          </button>
        </div>

        <div
          ref="logPane"
          class="panel-scrollbar max-h-[28rem] overflow-auto rounded-2xl bg-zinc-950/90 p-4 font-mono text-[11px] leading-5 text-zinc-200"
        >
          <div v-if="!activeLogLines.length" class="text-zinc-500">{{ __('Log is empty.') }}</div>
          <div v-for="(line, idx) in activeLogLines" :key="idx" class="whitespace-pre-wrap break-all">
            {{ line }}
          </div>
        </div>

        <p v-if="activeLogPath" class="truncate text-[10px] text-zinc-400" :title="activeLogPath">
          {{ activeLogPath }}
        </p>
      </div>
    </article>
  </div>
</template>

<script>
export default {
  props: ['card'],

  data() {
    return {
      data: null,
      loading: false,
      error: '',
      actionMessage: '',
      selectedLog: null,
      autoScroll: true,
      timer: null,
      restartingKey: null,
      clearingLog: false,
    }
  },

  computed: {
    cpuPercent() {
      return this.data?.cpu?.usage_percent ?? null
    },
    memPercent() {
      return this.data?.memory?.usage_percent ?? null
    },
    diskPercent() {
      return this.data?.disk?.usage_percent ?? null
    },
    overallOk() {
      const workers = this.data?.workers?.items || []
      if (!workers.length) return true
      return workers.every(w => w.active)
    },
    updatedLabel() {
      if (!this.data?.generated_at) return ''
      try {
        return new Date(this.data.generated_at).toLocaleTimeString()
      } catch (e) {
        return ''
      }
    },
    activeLog() {
      const logs = this.data?.logs || []
      if (!logs.length) return null
      return logs.find(l => l.key === this.selectedLog) || logs[0]
    },
    activeLogLines() {
      return this.activeLog?.lines || []
    },
    activeLogPath() {
      return this.activeLog?.path || ''
    },
  },

  watch: {
    activeLogLines() {
      if (!this.autoScroll) return
      this.$nextTick(() => {
        const pane = this.$refs.logPane
        if (pane) pane.scrollTop = pane.scrollHeight
      })
    },
  },

  mounted() {
    this.fetchSnapshot()
    this.timer = setInterval(() => this.fetchSnapshot({ silent: true }), 5000)
  },

  beforeUnmount() {
    clearInterval(this.timer)
  },

  methods: {
    meterWidth(value) {
      if (value == null || Number.isNaN(Number(value))) return '0%'
      return `${Math.max(0, Math.min(100, Number(value)))}%`
    },
    toneClass(value) {
      const v = Number(value)
      if (Number.isNaN(v)) return 'text-zinc-400'
      if (v >= 85) return 'text-rose-500'
      if (v >= 65) return 'text-amber-500'
      return 'text-emerald-500'
    },
    barClass(value) {
      const v = Number(value)
      if (Number.isNaN(v)) return 'bg-zinc-400'
      if (v >= 85) return 'bg-rose-500'
      if (v >= 65) return 'bg-amber-500'
      return 'bg-emerald-500'
    },
    formatMb(value) {
      if (value == null) return '—'
      if (value >= 1024) return `${(value / 1024).toFixed(1)} GB`
      return `${value} MB`
    },
    actionMeta(key) {
      return (this.data?.actions || []).find(a => a.key === key) || null
    },
    async restart(key) {
      const meta = this.actionMeta(key)
      const confirmText = meta?.confirm || this.__('Restart this service now?')
      if (!window.confirm(confirmText)) {
        return
      }

      this.restartingKey = key
      this.error = ''
      this.actionMessage = ''
      try {
        const { data } = await Nova.request().post('/nova-api/system-health/restart', { key })
        this.actionMessage = data?.message || this.__('Service restarted.')
        if (typeof Nova.success === 'function') {
          Nova.success(this.actionMessage)
        }
        await this.fetchSnapshot({ silent: true })
      } catch (e) {
        const message = e?.response?.data?.message || e.message || this.__('Failed to restart service.')
        this.error = message
        if (typeof Nova.error === 'function') {
          Nova.error(message)
        }
      } finally {
        this.restartingKey = null
      }
    },
    async clearLaravelLog() {
      if (!window.confirm(this.__('Clear the Laravel log file? This cannot be undone.'))) {
        return
      }

      this.clearingLog = true
      this.error = ''
      this.actionMessage = ''
      try {
        const { data } = await Nova.request().post('/nova-api/system-health/clear-laravel-log')
        this.actionMessage = data?.message || this.__('Laravel log cleared.')
        if (typeof Nova.success === 'function') {
          Nova.success(this.actionMessage)
        }
        this.selectedLog = 'laravel'
        await this.fetchSnapshot({ silent: true })
      } catch (e) {
        const message = e?.response?.data?.message || e.message || this.__('Could not clear the Laravel log (permission denied).')
        this.error = message
        if (typeof Nova.error === 'function') {
          Nova.error(message)
        }
      } finally {
        this.clearingLog = false
      }
    },
    async fetchSnapshot(options = {}) {
      if (!options.silent) this.loading = true
      this.error = ''
      try {
        const { data } = await Nova.request().get('/nova-api/system-health')
        this.data = data
        if (!this.selectedLog && data?.logs?.length) {
          this.selectedLog = data.logs[0].key
        }
      } catch (e) {
        this.error = e?.response?.data?.message || e.message || __('Failed to load system health.')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
